// Theme (light/dark) — Phase 2 design system.
//
// Dark mode is stamped as `data-theme="dark|light"` on <html> so CSS reads
// it via `:root[data-theme="dark"]` (see styles/globals.css). Resolution:
//   1. an explicit user choice saved in localStorage, else
//   2. the OS `prefers-color-scheme`.
//
// `initTheme()` MUST run synchronously in main.tsx before React renders so
// the first paint is already the right theme (no light-mode flash). The
// <ThemeToggle> in the Topbar flips + persists the choice at runtime.

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'fnsai.theme';

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/** The user's saved choice, or null if they've never toggled. */
export function storedTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

/** The theme that should apply right now (saved choice ?? system). */
export function resolveTheme(): Theme {
  return storedTheme() ?? (systemPrefersDark() ? 'dark' : 'light');
}

/** The theme currently painted (reads the <html> attribute). */
export function currentTheme(): Theme {
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'dark' ? 'dark' : 'light';
}

/** Paint a theme now. Notifies listeners via a `themechange` event. */
export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  // Keep the native form-control / scrollbar palette in step.
  document.documentElement.style.colorScheme = theme;
  try {
    window.dispatchEvent(new CustomEvent<Theme>('fnsai:themechange', { detail: theme }));
  } catch {
    /* CustomEvent unsupported — non-fatal */
  }
}

/** Persist + paint an explicit user choice. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* private mode — session-only, still paints */
  }
  applyTheme(theme);
}

/** Flip and persist. Returns the new theme. */
export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

/** Run once, as early as possible, before first paint. */
export function initTheme(): void {
  applyTheme(resolveTheme());
  // If the user has NOT made an explicit choice, track the OS preference live.
  try {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (storedTheme() === null) applyTheme(mq.matches ? 'dark' : 'light');
    };
    mq.addEventListener?.('change', onChange);
  } catch {
    /* matchMedia unsupported — static theme is fine */
  }
}
